import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import {
  Target, Clock, Sparkles, Building, UserCircle,
  Lightbulb, ArrowRight, X, Zap, ShieldCheck, Check, XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ICON_MAP = {
  target: Target, clock: Clock, sparkles: Sparkles,
  building: Building, 'user-circle': UserCircle, lightbulb: Lightbulb,
};

const SEVERITY_COLORS = {
  action: '#D97706',
  warning: '#F59E0B',
  info: '#3B82F6',
};

const EmmaInsightsWidget = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [insights, setInsights] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/emma-proactive/insights?language=${lang}`, { headers }).catch(() => ({ data: { insights: [] } })),
      axios.get(`${API}/emma-proactive/approvals`, { headers }).catch(() => ({ data: { approvals: [] } })),
    ]).then(([insRes, appRes]) => {
      setInsights((insRes.data.insights || []).slice(0, 3));
      setApprovals((appRes.data.approvals || []).slice(0, 2));
      setLoaded(true);
    });
  }, [token, lang]);

  const dismissInsight = async (id) => {
    setInsights(prev => (Array.isArray(prev) ? prev : []).filter(i => i.id !== id));
    try {
      await axios.post(`${API}/emma-proactive/insights/${id}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* silent */ }
  };

  const respondApproval = async (id, decision) => {
    setApprovals(prev => (Array.isArray(prev) ? prev : []).filter(a => a.id !== id));
    try {
      await axios.post(`${API}/emma-proactive/approvals/${id}/respond`, { decision }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) { /* silent */ }
  };

  if (!loaded || (insights.length === 0 && approvals.length === 0)) return null;

  return (
    <div className="eiw-container" data-testid="emma-insights-widget">
      <div className="eiw-header">
        <div className="eiw-header-left">
          <Zap size={18} style={{ color: '#D97706' }} />
          <h3 className="eiw-title">
            {lang === 'nl' ? 'Emma Inzichten' : 'Emma Insights'}
          </h3>
        </div>
        <button
          className="eiw-see-all"
          onClick={() => navigate('/portal/emma-proactive')}
          data-testid="eiw-see-all-btn"
        >
          {lang === 'nl' ? 'Alles bekijken' : 'View all'}
          <ArrowRight size={14} />
        </button>
      </div>

      <div className="eiw-cards">
        {/* Insight Cards */}
        {(Array.isArray(insights) ? insights : []).map((insight) => {
          const Icon = ICON_MAP[insight.icon] || Lightbulb;
          const color = SEVERITY_COLORS[insight.severity] || '#3B82F6';
          return (
            <div
              key={insight.id}
              className="eiw-card"
              style={{ borderLeftColor: color }}
              data-testid={`eiw-insight-${insight.id}`}
            >
              <div className="eiw-card-row">
                <Icon size={16} style={{ color, flexShrink: 0 }} />
                <p className="eiw-card-msg">{insight.message}</p>
                <button className="eiw-card-dismiss" onClick={() => dismissInsight(insight.id)} data-testid={`eiw-dismiss-${insight.id}`}>
                  <X size={12} />
                </button>
              </div>
              {insight.action_route && (
                <button
                  className="eiw-card-action"
                  onClick={() => navigate(insight.action_route)}
                  data-testid={`eiw-action-${insight.id}`}
                >
                  {insight.suggested_action} <ArrowRight size={12} />
                </button>
              )}
            </div>
          );
        })}

        {/* Approval Cards */}
        {(Array.isArray(approvals) ? approvals : []).map((approval) => (
          <div key={approval.id} className="eiw-card eiw-approval" data-testid={`eiw-approval-${approval.id}`}>
            <div className="eiw-card-row">
              <ShieldCheck size={16} style={{ color: '#7C3AED', flexShrink: 0 }} />
              <p className="eiw-card-msg">
                {lang === 'nl' ? approval.description_nl : approval.description_en}
              </p>
            </div>
            <div className="eiw-approval-actions">
              <button className="eiw-approve" onClick={() => respondApproval(approval.id, 'approved')} data-testid={`eiw-approve-${approval.id}`}>
                <Check size={12} /> {lang === 'nl' ? 'Ja' : 'Yes'}
              </button>
              <button className="eiw-deny" onClick={() => respondApproval(approval.id, 'denied')} data-testid={`eiw-deny-${approval.id}`}>
                <XCircle size={12} /> {lang === 'nl' ? 'Nee' : 'No'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmmaInsightsWidget;
